import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { parseTime } from "../util_functions/helpers";
import config from "../config/config";

const JobCreateContext = createContext();


const defaultSupportJobLocationDetails = {
  supportJobLocation: "",
  preferredGender: "",
};

const defaultFrequencyDetails = {
  jobType: "",
  shiftTypes: [],
  supportType: "",

  payType: "",
  payRateHourly: null,
  payRateDaily: null,
  payRateAnnual: null,
  currency: "USD",

  payRanges: {
    hourly: { min: null, max: null },
    daily: { min: null, max: null },
    annual: { min: null, max: null },
  },

  sessionsPerWeek: 0,
  hoursPerSession: 0,
  supportStartDate: "",
  scheduling: "",
  additionalDetails: "",

  preferredDays: {
    Monday: false, Tuesday: false, Wednesday: false,
    Thursday: false, Friday: false, Saturday: false, Sunday: false,
  },
  timeSlots: {
    Monday: [], Tuesday: [], Wednesday: [],
    Thursday: [], Friday: [], Saturday: [], Sunday: [],
  },

  sessionRanges: [],
  totalHoursPerWeek: null,
};


const defaultJobCraftDetails = {
  jobTitle: "",
  jobDescription: "",
  preferredQualities: "",
  personalDetails: "",
};

const defaultJobCreateSelections = {
  selectedLocations: [],
  suburbValue: "",
  supportTypeDetails: [],
  careActivityDetails: [],
  supportJobLocationDetails: { ...defaultSupportJobLocationDetails },
  frequencyDetails: { ...defaultFrequencyDetails },
  jobCraftDetails: { ...defaultJobCraftDetails },
};


const deepMerge = (base, patch) => {
  if (patch == null) return base;
  if (Array.isArray(base) || Array.isArray(patch)) return patch ?? base;
  const out = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    const bv = out[k];
    out[k] =
      v && typeof v === "object" && !Array.isArray(v)
        ? deepMerge(bv ?? {}, v)
        : v;
  }
  return out;
};

const normalizeFrequency = (fd) => {
  const merged = deepMerge(defaultFrequencyDetails, fd || {});
  merged.shiftTypes = Array.isArray(merged.shiftTypes) ? merged.shiftTypes : [];

  const toNum = (x) =>
    typeof x === "string" && x.trim() !== ""
      ? Number.isNaN(Number(x)) ? x : Number(x)
      : x;

  merged.payRateHourly = toNum(merged.payRateHourly);
  merged.payRateDaily = toNum(merged.payRateDaily);
  merged.payRateAnnual = toNum(merged.payRateAnnual);
  merged.hoursPerSession = toNum(merged.hoursPerSession);
  merged.sessionsPerWeek = toNum(merged.sessionsPerWeek);

  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  merged.preferredDays = days.reduce((acc, d) => ({ ...acc, [d]: !!merged.preferredDays?.[d] }), {});
  merged.timeSlots = days.reduce((acc, d) => {
    const arr = Array.isArray(merged.timeSlots?.[d]) ? merged.timeSlots[d] : [];
    acc[d] = arr.filter(s => s && s.from && s.to);
    return acc;
  }, {});

  if (!Array.isArray(merged.sessionRanges) || merged.sessionRanges.length === 0) {
    const buildLabel = (mins) => {
      const h = Math.floor(mins / 60), m = mins % 60;
      const parts = [];
      if (h > 0) parts.push(`${h} hr${h === 1 ? "" : "s"}`);
      if (m > 0) parts.push(`${m} min${m === 1 ? "" : "s"}`);
      return parts.length ? parts.join(" ") : "0 mins";
    };
    const ranges = [];
    for (const d of days) {
      if (!merged.preferredDays[d]) continue;
      for (const s of merged.timeSlots[d] || []) {
        const from = parseTime(s.from), to = parseTime(s.to);
        if (Number.isFinite(from) && Number.isFinite(to) && to > from) {
          const minutes = to - from;
          ranges.push({ day: d, from: s.from, to: s.to, minutes, label: buildLabel(minutes) });
        }
      }
    }
    merged.sessionRanges = ranges;
  }

  return merged;
};


const normalizeSelections = (raw) => {
  const data = raw || {};

  const incomingJobCraft =
    data.jobCraftDetails || data.jobcraftDetails || defaultJobCraftDetails;

  const normalized = {
    ...defaultJobCreateSelections,
    ...data,
    supportJobLocationDetails: deepMerge(
      defaultSupportJobLocationDetails,
      data.supportJobLocationDetails
    ),
    frequencyDetails: normalizeFrequency(data.frequencyDetails),
    jobCraftDetails: deepMerge(defaultJobCraftDetails, incomingJobCraft),
  };

  normalized.selectedLocations = Array.isArray(data?.selectedLocations)
    ? data.selectedLocations.filter(Boolean)
    : data?.suburbValue
      ? [data.suburbValue]
      : [];
  normalized.suburbValue = normalized.selectedLocations[0] || "";

  if ("jobcraftDetails" in normalized) delete normalized.jobcraftDetails;

  return normalized;
};

export const JobCreateProvider = ({ children }) => {
  const [jobSelections, setJobSelections] = useState(
    defaultJobCreateSelections
  );
  const [isJobSelectionsReady, setIsJobSelectionsReady] = useState(false);
  const [jobCreateId, setJobCreateId] = useState(() => {
    const match = document.cookie.match(/jobCreateId=([^;]+)/);
    return match ? match[1] : null;
  });

  const getJobCreateIdFromCookies = useCallback(() => {
    const match = document.cookie.match(/jobCreateId=([^;]+)/);
    return match ? match[1] : null;
  }, []);

  const fetchJobSelectionsById = useCallback(async (id) => {
    try {
      const { data } = await axios.get(
        `${config.API_BASE_URL}/v1/job/create/${id}`
      );
      const normalized = normalizeSelections(data);
      setJobSelections(normalized);
      setIsJobSelectionsReady(true);
      return normalized;
    } catch (e) {
      console.error("[fetchJobSelectionsById] Error:", e);
      setJobSelections(defaultJobCreateSelections);
      setIsJobSelectionsReady(true);
      return defaultJobCreateSelections;
    }
  }, []);

  useEffect(() => {
    const cookieId = getJobCreateIdFromCookies();
    if (cookieId) {
      setJobCreateId(cookieId);
      fetchJobSelectionsById(cookieId).catch((error) => {
        console.error("Failed to fetch job selections from API:", error);
      });
    } else {
      console.log("No jobCreateId found in cookies.");
      setIsJobSelectionsReady(true);
    }
  }, [getJobCreateIdFromCookies, fetchJobSelectionsById]);

  const updateJobSelectionField = useCallback(
    async (field, value) => {
      let normalizedValue = value;
      let targetField = field;

      if (field === "frequencyDetails") {
        normalizedValue = normalizeFrequency(value);
      } else if (field === "supportJobLocationDetails") {
        normalizedValue = deepMerge(defaultSupportJobLocationDetails, value || {});
      } else if (field === "jobCraftDetails" || field === "jobcraftDetails") {
        targetField = "jobCraftDetails";
        normalizedValue = deepMerge(defaultJobCraftDetails, value || {});
      } else if (field === "selectedLocations" && Array.isArray(value)) {
        normalizedValue = value.filter(Boolean);
      }

      const nextSelections = deepMerge(jobSelections, {
        [targetField]: normalizedValue,
      });
      setJobSelections(nextSelections);

      if (jobCreateId) {
        try {
          await axios.patch(
            `${config.API_BASE_URL}/v1/job/create/${jobCreateId}`,
            { [targetField]: normalizedValue }
          );
        } catch (error) {
          console.error(`Error updating field ${targetField} in Redis:`, error);
        }
      } else {
        console.error("Job ID is missing; cannot patch the selection.");
      }
    },
    [jobSelections, jobCreateId]
  );

  const saveJobSelections = useCallback(
    async (selections, isFirstStep = false) => {
      const sel = normalizeSelections(selections || {});
      const existingJobCreateId = getJobCreateIdFromCookies();
      console.log("[saveJobSelections] cookie draftId:", existingJobCreateId, {
        isFirstStep,
      });

      try {
        if (!existingJobCreateId && isFirstStep) {
          console.log("[saveJobSelections] → POST new job");
          const { data } = await axios.post(
            `${config.API_BASE_URL}/v1/job/create`,
            sel
          );
          const newJobId = data.key;
          console.log(
            "[saveJobSelections] → POST succeeded, newJobId:",
            newJobId
          );
          setJobCreateId(newJobId);
          Cookies.set("jobCreateId", newJobId, { path: "/" });
          setJobSelections(sel);
          setIsJobSelectionsReady(true);
          return newJobId;
        }

        console.log(
          "[saveJobSelections] → PATCH existing job:",
          existingJobCreateId
        );

        let existing;
        try {
          existing = await fetchJobSelectionsById(existingJobCreateId);
        } catch {
          existing = null;
        }

        if (!existing && isFirstStep) {
          console.log(
            "[saveJobSelections] fetch failed; falling back to POST"
          );
          const { data } = await axios.post(
            `${config.API_BASE_URL}/v1/job/create`,
            sel
          );
          const newJobId = data.key;
          console.log("[saveJobSelections] → fallback POST succeeded:", newJobId);

          setJobCreateId(newJobId);
          document.cookie = `jobCreateId=${newJobId}; path=/`;
          setJobSelections(sel);
          setIsJobSelectionsReady(true);
          return newJobId;
        }

        const fieldsToCheck = [
          "selectedLocations",
          "suburbValue",
          "supportTypeDetails",
          "careActivityDetails",
          "supportJobLocationDetails",
          "frequencyDetails",
          "jobCraftDetails",
        ];

        for (const field of fieldsToCheck) {
          const newValue = sel[field];
          const oldValue = existing ? existing[field] : undefined;
          if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            console.log(`[saveJobSelections] patching field ${field}:`, newValue);
            await updateJobSelectionField(field, newValue);
          }
        }

        console.log("[saveJobSelections] PATCH complete for", jobCreateId);

        Cookies.set("jobCreateId", existingJobCreateId, { path: "/" });
        setIsJobSelectionsReady(true);
        return existingJobCreateId;
      } catch (error) {
        console.error("[saveJobSelections] ERROR:", error);
        throw error;
      }
    },
    [fetchJobSelectionsById, getJobCreateIdFromCookies, jobCreateId, updateJobSelectionField]
  );

  const refreshDraft = useCallback(async () => {
    const id = getJobCreateIdFromCookies();
    if (id) return await fetchJobSelectionsById(id);
    return null;
  }, [getJobCreateIdFromCookies, fetchJobSelectionsById]);


  const clearJobSelections = useCallback(() => {
    console.log(`Clearing job selections. Current ID: ${jobCreateId}`);
    setJobSelections(defaultJobCreateSelections);
    setIsJobSelectionsReady(false);

    if (jobCreateId) {
      try {
        axios.delete(`${config.API_BASE_URL}/v1/job/create/${jobCreateId}`);
      } catch (error) {
        console.error(
          `Failed to clear job selections for ID: ${jobCreateId}`,
          error
        );
      }
    }
    Cookies.remove("jobCreateId", { path: "/" });
    setJobCreateId(null);
  }, [jobCreateId]);

  return (
    <JobCreateContext.Provider
      value={{
        jobSelections,
        isJobSelectionsReady,
        getJobCreateIdFromCookies,
        fetchJobSelectionsById,
        saveJobSelections,
        updateJobSelectionField,
        refreshDraft, 
        clearJobSelections,
      }}
    >
      {children}
    </JobCreateContext.Provider>
  );
};

export const useJobCreate = () => useContext(JobCreateContext);

